import { keepPreviousData, QueryClient, queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  ApprovalDto,
  ApprovalKind,
  DependenciesDto,
  SavedViewDto,
  PortfolioDto,
  NotifyPrefsDto,
  ReminderDto,
  ShareLinkDto,
  DuplicateInfoDto,
  TemplateDto,
  CalendarDto,
  CalendarFeedDto,
  GuestsDto,
  TaskTablePageDto,
  ActivityPageDto,
  AttachmentDto,
  ChecklistItemDto,
  FileDto,
  FileVersionDto,
  FolderContentDto,
  FileQueryResultDto,
  FileTagDto,
  FileViewState,
  TaskAttachmentEntryDto,
  PageCommentDto,
  ChatPageDto,
  TalkStateDto,
  TopicDetailDto,
  TopicListItemDto,
  PageDetailDto,
  PageDto,
  PageVersionDto,
  CommentPageDto,
  FollowDto,
  InvitationDto,
  MeDto,
  MemberDto,
  MyDayDto,
  SearchResultDto,
  NotificationPageDto,
  ProjectCardDto,
  ProjectOverviewDto,
  NextStepsDto,
  ProjectSignalsDto,
  ProjectSnapshotDto,
  PublicConfigDto,
  TaskDto,
  TaskPageDto,
  WorkspaceDto,
  PinKind,
  TaskLinkDto,
  LinkKind,
  LinkedItemDto,
  LinkRoleDto,
} from '@werft/contracts';
import { api, ApiError } from './api.js';
import { rememberFormer } from './names.js';
import { today } from '../i18n/index.js';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Daten gelten 30 s als frisch (wie KaBa); Live-Ereignisse aktualisieren gezielt
      staleTime: 30_000,
      retry: (n, e) => !(e instanceof ApiError && e.status > 0 && e.status < 500) && n < 3,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Query-Keys enthalten den Workspace-Kontext (Leitlinie, Seite 7). Entzug oder Abmeldung räumt alles unter
 * ['ws', id] bzw. den gesamten Cache.
 */
export const keys = {
  config: ['config'] as const,
  me: ['me'] as const,
  workspace: (id: string) => ['ws', id] as const,
  members: (id: string) => ['ws', id, 'members'] as const,
  invitations: (id: string) => ['ws', id, 'invitations'] as const,
  adminWorkspaces: ['admin', 'workspaces'] as const,
  projectCards: (wsId: string, archived = false) => ['ws', wsId, 'projects', archived ? 'archived' : 'active'] as const,
  /** Alles eines Projekts liegt unter ['p', id] – Entzug räumt es in einem Schritt */
  project: (id: string) => ['p', id] as const,
  snapshot: (id: string) => ['p', id, 'snapshot'] as const,
  tasks: (id: string, state: 'open' | 'done') => ['p', id, 'tasks', state] as const,
  task: (projectId: string, taskId: string) => ['p', projectId, 'task', taskId] as const,
  comments: (projectId: string, taskId: string) => ['p', projectId, 'comments', taskId] as const,
  attachments: (projectId: string, taskId: string) => ['p', projectId, 'attachments', taskId] as const,
  overview: (projectId: string) => ['p', projectId, 'overview'] as const,
  pins: (projectId: string) => ['p', projectId, 'pins'] as const,
  /** Ablage (P11): Inhalt je Ordner ('root' = oberste Ebene), Papierkorb, Anhänge an Aufgaben, Fassungen, Dateien einer Aufgabe */
  files: (projectId: string, folderId: string | null) => ['p', projectId, 'files', folderId ?? 'root'] as const,
  fileTrash: (projectId: string) => ['p', projectId, 'filetrash'] as const,
  fileAttachments: (projectId: string) => ['p', projectId, 'fileattachments'] as const,
  fileVersions: (projectId: string, fileId: string) => ['p', projectId, 'fileversions', fileId] as const,
  fileComments: (projectId: string, fileId: string) => ['p', projectId, 'filecomments', fileId] as const,
  taskFiles: (projectId: string, taskId: string) => ['p', projectId, 'taskfiles', taskId] as const,
  /** Notizen (P12): Seiten eines Projekts, Papierkorb, Kopf einer Seite, Versionen, Kommentare */
  pages: (projectId: string) => ['p', projectId, 'pages'] as const,
  pageTrash: (projectId: string) => ['p', projectId, 'pagetrash'] as const,
  page: (projectId: string, pageId: string) => ['p', projectId, 'page', pageId] as const,
  pageVersions: (projectId: string, pageId: string) => ['p', projectId, 'pageversions', pageId] as const,
  pageComments: (projectId: string, pageId: string) => ['p', projectId, 'pagecomments', pageId] as const,
  /** Gespräche (P13): mein Stand, Themen (offen/erledigt), ein Thema, Chat (seitenweise) */
  talk: (projectId: string) => ['p', projectId, 'talk'] as const,
  topics: (projectId: string, state: 'open' | 'resolved') => ['p', projectId, 'topics', state] as const,
  topic: (projectId: string, topicId: string) => ['p', projectId, 'topic', topicId] as const,
  chat: (projectId: string) => ['p', projectId, 'chat'] as const,
  subtasks: (projectId: string, taskId: string) => ['p', projectId, 'subtasks', taskId] as const,
  checklist: (projectId: string, taskId: string) => ['p', projectId, 'checklist', taskId] as const,
  follow: (projectId: string, taskId: string) => ['p', projectId, 'follow', taskId] as const,
  /** P25: gespeicherte Ansichten der Tabelle */
  views: (projectId: string) => ['p', projectId, 'views'] as const,
  /** P23: persönliche Erinnerung an einer Aufgabe */
  reminder: (projectId: string, taskId: string) => ['p', projectId, 'reminder', taskId] as const,
  /** P23: Benachrichtigungswege (E-Mail-Zusammenfassung, Push, Geräte) */
  notifyPrefs: ['me', 'notify'] as const,
  activity: (projectId: string, taskId?: string) => ['p', projectId, 'activity', taskId ?? 'alle'] as const,
  /** Persönlich, projektübergreifend */
  notifications: ['inbox'] as const,
  myDay: ['inbox', 'day'] as const,
  search: (q: string, projectId?: string) => ['search', q, projectId ?? ''] as const,
  unread: ['inbox', 'unread'] as const,
};

export interface Snapshot<T> {
  items: T[];
  cursor: number;
}

export const useConfig = () => useQuery({ queryKey: keys.config, queryFn: () => api.get<PublicConfigDto>('/api/config'), staleTime: 60_000 });

export const useMe = () =>
  useQuery({
    queryKey: keys.me,
    queryFn: async () => {
      try {
        return await api.get<MeDto>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
  });

export const useWorkspace = (id: string) => useQuery({ queryKey: keys.workspace(id), queryFn: () => api.get<WorkspaceDto>(`/api/workspaces/${id}`) });
export const useMembers = (id: string, enabled = true) =>
  useQuery({ queryKey: keys.members(id), queryFn: () => api.get<Snapshot<MemberDto>>(`/api/workspaces/${id}/members`), enabled });
/** Gäste eines Projekts (P15, nur Projektleitung) – unter dem Projekt-Schlüssel, damit Zugriffswechsel neu laden */
export const useGuests = (projectId: string, enabled: boolean) =>
  useQuery({ queryKey: ['p', projectId, 'guests'] as const, queryFn: () => api.get<GuestsDto>(`/api/projects/${projectId}/guests`), enabled });
export const useInvitations = (id: string, enabled: boolean) =>
  useQuery({ queryKey: keys.invitations(id), queryFn: () => api.get<Snapshot<InvitationDto>>(`/api/workspaces/${id}/invitations`), enabled });
export const useAdminWorkspaces = (enabled: boolean) =>
  useQuery({ queryKey: keys.adminWorkspaces, queryFn: () => api.get<{ items: WorkspaceDto[] }>('/api/admin/workspaces'), enabled });

/** P24: Zeitachse der Lobby – nicht offline gespeichert, beim Öffnen frisch */
export const usePortfolio = (wsId: string, enabled: boolean) =>
  useQuery({ queryKey: ['ws', wsId, 'portfolio'] as const, queryFn: () => api.get<PortfolioDto>(`/api/workspaces/${wsId}/portfolio`), enabled, staleTime: 15_000 });

export const useProjectCards = (wsId: string, archived = false) =>
  useQuery({ queryKey: keys.projectCards(wsId, archived), queryFn: () => api.get<{ items: ProjectCardDto[] }>(`/api/workspaces/${wsId}/projects${archived ? '?archived=1' : ''}`) });

/** P17: Vorlagen im Workspace (Schlüssel unter „projects“ → Live-Abgleich lädt mit den Karten neu) */
export const useTemplates = (wsId: string, enabled = true) =>
  useQuery({ queryKey: ['ws', wsId, 'projects', 'templates'] as const, enabled, queryFn: () => api.get<{ items: TemplateDto[] }>(`/api/workspaces/${wsId}/templates`) });

export const useDuplicateInfo = (pid: string, enabled: boolean) =>
  useQuery({ queryKey: ['dup', pid] as const, enabled, staleTime: 0, gcTime: 0, queryFn: () => api.get<DuplicateInfoDto>(`/api/projects/${pid}/duplicate-info`) });

export const useProjectSnapshot = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.snapshot(id),
    queryFn: async () => {
      const s = await api.get<ProjectSnapshotDto>(`/api/projects/${id}/snapshot`);
      rememberFormer(s.formerMembers);
      return s;
    },
    enabled,
  });

/** Aufgaben seitenweise (200 je Seite); weitere Seiten lädt die Liste beim Scrollen nach */
export const useTasks = (id: string, state: 'open' | 'done', enabled = true) =>
  useInfiniteQuery({
    queryKey: keys.tasks(id, state),
    queryFn: ({ pageParam }) => api.get<TaskPageDto>(`/api/projects/${id}/tasks?state=${state}&limit=200${pageParam ? `&after=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
  });

/** Tabelle (P16): Server sortiert und filtert; Seiten über `offset` */
export const useTaskTable = (projectId: string, qs: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: ['p', projectId, 'table', qs] as const,
    queryFn: ({ pageParam }) => api.get<TaskTablePageDto>(`/api/projects/${projectId}/table?${qs}&limit=100&offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const n = all.reduce((sum, p) => sum + p.items.length, 0);
      return n < last.total ? n : undefined;
    },
    enabled,
    placeholderData: (prev) => prev,
  });

/** Kalender (P16): Aufgaben mit Termin im Zeitraum */
export const useCalendar = (projectId: string, from: string, to: string, lists: string, enabled = true, span = false) =>
  useQuery({
    queryKey: ['p', projectId, 'calendar', from, to, lists, span ? 'span' : 'due'] as const,
    queryFn: () => api.get<CalendarDto>(`/api/projects/${projectId}/calendar?from=${from}&to=${to}${lists ? `&lists=${lists}` : ''}${span ? '&span=1' : ''}`),
    enabled,
    placeholderData: (prev) => prev,
  });

export const useCalendarFeed = (projectId: string, enabled: boolean) =>
  useQuery({ queryKey: ['p', projectId, 'calendar-feed'] as const, queryFn: () => api.get<CalendarFeedDto>(`/api/projects/${projectId}/calendar-feed`), enabled });

/** P20: Freigabelinks – bewusst NICHT unter 'p' (enthalten Adressen; werden nicht offline gespeichert) */
export const useShares = (projectId: string, targetId: string | null, enabled = true) =>
  useQuery({
    queryKey: ['shares', projectId, targetId ?? 'all'] as const,
    enabled,
    // Aufrufe von außen erzeugen kein Live-Ereignis: beim Öffnen immer frisch
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () => api.get<{ items: ShareLinkDto[] }>(`/api/projects/${projectId}/shares${targetId ? `?target=${targetId}` : ''}`),
  });

/** Einzelne Aufgabe (Detailansicht, auch über teilbare URL); Live-Ereignisse halten sie aktuell */
export const useTask = (projectId: string, taskId: string | undefined) =>
  useQuery({ queryKey: keys.task(projectId, taskId ?? ''), queryFn: () => api.get<TaskDto>(`/api/tasks/${taskId}`), enabled: !!taskId });

/** Kommentare einer Aufgabe: neueste Seite zuerst, „ältere laden“ hängt vorne an */
export const useComments = (projectId: string, taskId: string) =>
  useInfiniteQuery({
    queryKey: keys.comments(projectId, taskId),
    queryFn: ({ pageParam }) => api.get<CommentPageDto>(`/api/tasks/${taskId}/comments?limit=50${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.before ?? undefined,
  });

export const useAttachments = (projectId: string, taskId: string) =>
  useQuery({ queryKey: keys.attachments(projectId, taskId), queryFn: () => api.get<{ items: AttachmentDto[] }>(`/api/tasks/${taskId}/attachments`) });

/** Projektübersicht (P9): nächste Termine, Angeheftetes, Fortschritt; `today` = Kalendertag der Person */
/** Audit F10: verknüpfte Inhalte (Aufgabe, Seite, Datei, Thema) */
export const useLinks = (projectId: string, kind: LinkKind, id: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'links', kind, id] as const, queryFn: () => api.get<{ items: LinkedItemDto[]; openRoles?: LinkRoleDto[] }>(`/api/projects/${projectId}/links?kind=${kind}&id=${id}`), enabled, staleTime: 5_000 });

/** ADR 0135: darf die Person Personendaten (und damit Beteiligte/Rollen) sehen? */
export const usePeopleAllowed = (projectId: string, enabled = true) =>
  !!useQuery({ queryKey: ['p', projectId, 'people', 'access'] as const, queryFn: () => api.get<{ allowed: boolean }>(`/api/projects/${projectId}/people/access`), enabled, staleTime: 60_000 }).data?.allowed;

/** Etappe C: Abnahmen eines Inhalts (neueste zuerst) */
export const useApprovals = (projectId: string, kind: ApprovalKind, id: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'approvals', kind, id] as const, queryFn: () => api.get<{ items: ApprovalDto[] }>(`/api/projects/${projectId}/approvals?kind=${kind}&id=${id}`), enabled, staleTime: 5_000 });
/** Etappe C: eine Datei direkt (Einstieg aus Benachrichtigung oder Abnahme, wenn der Ordner nicht geladen ist) */
export const useFile = (projectId: string, fileId: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: ['p', projectId, 'file', fileId] as const, queryFn: () => api.get<FileDto>(`/api/files/${fileId}`), enabled: enabled && !!fileId });

/** Audit F10: Rückverweise einer Aufgabe (Thema, Antwort, Chat, verlinkende Seiten) */
export const useTaskLinks = (projectId: string, taskId: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'taskLinks', taskId] as const, queryFn: () => api.get<{ items: TaskLinkDto[] }>(`/api/tasks/${taskId}/links`), enabled, staleTime: 5_000 });

/** Audit F07: angeheftete Seiten, Dateien und Themen (nur IDs – für „Anheften“/„Lösen“ in den Werkzeugen) */
export const usePins = (projectId: string, enabled = true) =>
  useQuery({ queryKey: keys.pins(projectId), queryFn: () => api.get<{ items: { kind: PinKind; targetId: string }[] }>(`/api/projects/${projectId}/pins`), enabled });

/** Projektinsel W12: begründete nächste Schritte (Übersicht und Gast-Einstieg teilen sich die Abfrage) */
export const useNextSteps = (projectId: string, area = 'alle', enabled = true) => useQuery({ ...qdefs.nextSteps(projectId, area), enabled });

/**
 * 0.158 (ADR 0164): gemeinsame Abfragedefinitionen für Abruf, Vorladen und Darstellung – Schlüssel, Parameter und
 * Frische sind so überall gleich. Leseabrufe reichen das `signal` weiter (veraltete Antworten werden verworfen).
 */
const areaQs = (area: string) => (area !== 'alle' ? `&area=${encodeURIComponent(area)}` : '');
export const qdefs = {
  nextSteps: (projectId: string, area = 'alle') =>
    queryOptions({ queryKey: ['p', projectId, 'nextsteps', area] as const, queryFn: ({ signal }) => api.get<NextStepsDto>(`/api/projects/${projectId}/next-steps?today=${today()}${areaQs(area)}`, { signal }), staleTime: 15_000 }),
  signals: (projectId: string, day: string, area = 'alle') =>
    queryOptions({ queryKey: ['p', projectId, 'signals', area, day] as const, queryFn: ({ signal }) => api.get<ProjectSignalsDto>(`/api/projects/${projectId}/signals?today=${day}${areaQs(area)}`, { signal }), staleTime: 15_000 }),
  overview: (projectId: string, day: string, area = 'alle') =>
    queryOptions({ queryKey: [...keys.overview(projectId), area] as const, queryFn: ({ signal }) => api.get<ProjectOverviewDto>(`/api/projects/${projectId}/overview?today=${day}${areaQs(area)}`, { signal }) }),
  folder: (projectId: string, folderId: string | null) =>
    queryOptions({ queryKey: keys.files(projectId, folderId), queryFn: ({ signal }) => api.get<FolderContentDto>(`/api/projects/${projectId}/files${folderId ? `?folder=${folderId}` : ''}`, { signal }) }),
  pages: (projectId: string) => queryOptions({ queryKey: keys.pages(projectId), queryFn: ({ signal }) => api.get<{ items: PageDto[]; home?: string | null }>(`/api/projects/${projectId}/pages`, { signal }) }),
};

/** 0.155 (ADR 0157): Signale der Werkzeuge für die Übersicht (Tageswechsel: `today` im Schlüssel) */
export const useSignals = (projectId: string, day: string, area = 'alle') => useQuery(qdefs.signals(projectId, day, area));

// Audit 0.36 F01: Übersicht für den gewählten Bereich
export const useOverview = (projectId: string, day: string, enabled = true, area = 'alle') => useQuery({ ...qdefs.overview(projectId, day, area), enabled });

/** Ablage (P11) */
export const useFolderContent = (projectId: string, folderId: string | null) => useQuery(qdefs.folder(projectId, folderId));
/** 0.149 (ADR 0151): sortieren/suchen/eingrenzen – der Server filtert und sortiert vor dem Seitenschnitt */
export const useFileQuery = (projectId: string, qs: string, enabled: boolean) =>
  useInfiniteQuery({
    queryKey: ['p', projectId, 'filequery', qs] as const,
    queryFn: ({ pageParam, signal }) => api.get<FileQueryResultDto>(`/api/projects/${projectId}/files/query?${qs}&limit=100&offset=${pageParam}`, { signal }),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const n = all.reduce((sum, p) => sum + p.files.length, 0);
      return n < last.total ? n : undefined;
    },
    placeholderData: keepPreviousData,
    enabled,
  });
/** 0.152 (ADR 0154): Tags der Ablage (nur für mich sichtbare Begriffe, Zähler nur Sichtbares) und Datei-Ansichten */
export const useFileTags = (projectId: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'filetags'] as const, queryFn: () => api.get<{ items: FileTagDto[] }>(`/api/projects/${projectId}/file-tags`).then((r) => r.items), enabled, staleTime: 30_000 });
export interface FileViewDto {
  id: string;
  name: string;
  state: FileViewState;
  shared: boolean;
  mine: boolean;
  version: number;
}
export const useFileViews = (projectId: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'fileviews'] as const, queryFn: () => api.get<{ items: FileViewDto[] }>(`/api/projects/${projectId}/file-views`).then((r) => r.items), enabled });
export const useFileTrash = (projectId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.fileTrash(projectId), queryFn: () => api.get<{ items: FileDto[] }>(`/api/projects/${projectId}/files/trash`), enabled });
export const useFileAttachments = (projectId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.fileAttachments(projectId), queryFn: () => api.get<{ items: TaskAttachmentEntryDto[] }>(`/api/projects/${projectId}/files/attachments`), enabled });
export const useFileVersions = (projectId: string, fileId: string) =>
  useQuery({ queryKey: keys.fileVersions(projectId, fileId), queryFn: () => api.get<{ items: FileVersionDto[] }>(`/api/files/${fileId}/versions`) });
export const useTaskFiles = (projectId: string, taskId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.taskFiles(projectId, taskId), queryFn: () => api.get<{ items: FileDto[] }>(`/api/tasks/${taskId}/files`), enabled });

/** Notizen (P12) */
export const usePages = (projectId: string) => useQuery(qdefs.pages(projectId));
export const usePageTrash = (projectId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.pageTrash(projectId), queryFn: () => api.get<{ items: PageDto[] }>(`/api/projects/${projectId}/pages/trash`), enabled });
export const usePage = (projectId: string, pageId: string | undefined, enabled = true) =>
  useQuery({ queryKey: keys.page(projectId, pageId ?? ''), queryFn: () => api.get<PageDetailDto>(`/api/pages/${pageId}`), enabled: !!pageId && enabled });
export const usePageVersions = (projectId: string, pageId: string, enabled: boolean) =>
  useQuery({ queryKey: keys.pageVersions(projectId, pageId), queryFn: () => api.get<{ items: PageVersionDto[] }>(`/api/pages/${pageId}/versions`), enabled, staleTime: 0 });
export const usePageComments = (projectId: string, pageId: string, enabled = true) =>
  useQuery({ queryKey: keys.pageComments(projectId, pageId), queryFn: () => api.get<{ items: PageCommentDto[] }>(`/api/pages/${pageId}/comments`), enabled });

/** Gespräche (P13) */
export const useTalkState = (projectId: string, enabled = true) =>
  useQuery({ queryKey: keys.talk(projectId), queryFn: () => api.get<TalkStateDto>(`/api/projects/${projectId}/talk`), enabled, staleTime: 60_000 });
export const useTopics = (projectId: string, state: 'open' | 'resolved', enabled = true) =>
  useQuery({ queryKey: keys.topics(projectId, state), queryFn: () => api.get<{ items: TopicListItemDto[] }>(`/api/projects/${projectId}/topics${state === 'resolved' ? '?state=resolved' : ''}`), enabled });
export const useTopic = (projectId: string, topicId: string | undefined) =>
  useQuery({ queryKey: keys.topic(projectId, topicId ?? ''), queryFn: () => api.get<TopicDetailDto>(`/api/topics/${topicId}`), enabled: !!topicId });
/** Chat: neueste Seite zuerst, „ältere laden“ hängt hinten an (angezeigt alt → neu) */
export const useChat = (projectId: string, enabled = true) =>
  useInfiniteQuery({
    queryKey: keys.chat(projectId),
    queryFn: ({ pageParam }) => api.get<ChatPageDto>(`/api/projects/${projectId}/chat?limit=50${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.before ?? undefined,
    enabled,
  });

/** Unteraufgaben und Checkliste einer Aufgabe (Paket P8); Live-Ereignisse halten sie aktuell */
export const useSubtasks = (projectId: string, taskId: string, enabled = true) =>
  useQuery({ queryKey: keys.subtasks(projectId, taskId), queryFn: () => api.get<{ items: TaskDto[] }>(`/api/tasks/${taskId}/subtasks`), enabled });
export const useChecklist = (projectId: string, taskId: string, enabled = true) =>
  useQuery({ queryKey: keys.checklist(projectId, taskId), queryFn: () => api.get<{ items: ChecklistItemDto[] }>(`/api/tasks/${taskId}/checklist`), enabled });

export const useFollow = (projectId: string, taskId: string) =>
  useQuery({ queryKey: keys.follow(projectId, taskId), queryFn: () => api.get<FollowDto>(`/api/tasks/${taskId}/follow`) });

export const useActivity = (projectId: string, taskId: string | undefined, enabled: boolean, area = 'alle') =>
  useInfiniteQuery({
    queryKey: area === 'alle' ? keys.activity(projectId, taskId) : ([...keys.activity(projectId, taskId), area] as const),
    queryFn: ({ pageParam }) =>
      api.get<ActivityPageDto>(`/api/projects/${projectId}/activity?limit=50${taskId ? `&task=${taskId}` : ''}${area !== 'alle' ? `&area=${encodeURIComponent(area)}` : ''}${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
  });

/** P26: Abhängigkeiten einer Aufgabe (beide Richtungen) */
export const useDependencies = (projectId: string, taskId: string, enabled = true) =>
  useQuery({ queryKey: ['p', projectId, 'deps', taskId] as const, queryFn: () => api.get<DependenciesDto>(`/api/tasks/${taskId}/dependencies`), enabled });

export const useSavedViews = (projectId: string, enabled = true) =>
  useQuery({ queryKey: keys.views(projectId), enabled, queryFn: () => api.get<{ items: SavedViewDto[] }>(`/api/projects/${projectId}/views`) });

export const useReminder = (projectId: string, taskId: string) =>
  useQuery({ queryKey: keys.reminder(projectId, taskId), queryFn: () => api.get<{ reminder: ReminderDto | null }>(`/api/tasks/${taskId}/reminder`) });

export const useNotifyPrefs = (enabled = true) => useQuery({ queryKey: keys.notifyPrefs, queryFn: () => api.get<NotifyPrefsDto>('/api/me/notify'), enabled });

export const useUnread = (enabled: boolean) =>
  useQuery({ queryKey: keys.unread, queryFn: () => api.get<{ unread: number }>('/api/notifications/unread'), enabled, staleTime: 60_000 });

export const useNotifications = (enabled: boolean) =>
  useInfiniteQuery({
    queryKey: keys.notifications,
    queryFn: ({ pageParam }) => api.get<NotificationPageDto>(`/api/notifications?limit=30${pageParam ? `&before=${pageParam}` : ''}`),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled,
  });

/** Mein Tag: liegt unter ['inbox'] – jede Eingangs-Nachricht (auch Vormerken in anderen Tabs) lädt neu */
export const useMyDay = () =>
  useQuery({
    queryKey: keys.myDay,
    queryFn: () => api.get<MyDayDto>(`/api/me/day?tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}`),
  });

/** Suche der Befehlspalette; mit `projectId` nur in diesem Projekt (Audit F16) */
export const useSearch = (q: string, projectId?: string) =>
  useQuery({
    queryKey: keys.search(q, projectId),
    queryFn: () => api.get<SearchResultDto>(`/api/search?q=${encodeURIComponent(q)}${projectId ? `&project=${projectId}` : ''}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });
